/**
 * Shared compiled-artifact plumbing used by every game kind except
 * top-down-collect, which keeps its own original inline template verbatim
 * (byte-identical output is a hard requirement of the kind-registry refactor).
 */

/** The exact sandbox CSP every compiled game ships, locked by a router test too. */
export const SANDBOX_CSP =
  "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data:; connect-src 'none'; worker-src 'none'; form-action 'none'; base-uri 'none'";

export const BRIDGE_MESSAGE_TYPE = "carcade-state-v1";

export function safeJson(value: unknown): string {
  return JSON.stringify(value).replace(/</g, "\\u003c").replace(/>/g, "\\u003e").replace(/&/g, "\\u0026");
}

export interface GameDocumentInput {
  /** Extra CSS appended after the shared reset. */
  readonly styleExtra: string;
  /** The document body's markup, before the trusted script tag. */
  readonly bodyHtml: string;
  /** Named consts injected before the IIFE; values are safely JSON-escaped. */
  readonly consts: Readonly<Record<string, unknown>>;
  /** The IIFE body; may reference any name declared in `consts`. */
  readonly scriptBody: string;
}

/**
 * Assembles the shared CSP-locked single-file document shell every non-t-d-c
 * kind compiles into. Only validated parameters and, for quiz, validated
 * model-authored text ever reach here -- never model-authored code.
 */
export function renderGameDocument(input: GameDocumentInput): string {
  const declarations = Object.entries(input.consts)
    .map(([name, value]) => `${name}=${safeJson(value)}`)
    .join(",");
  // Declarations live INSIDE the IIFE: nothing leaks to the global scope,
  // and a same-document rewrite (test harnesses drive artifacts that way)
  // can never collide on redeclared top-level bindings.
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<meta http-equiv="Content-Security-Policy" content="${SANDBOX_CSP}">
<title>CARCADE game</title><style>
html,body{margin:0;height:100%;background:#000;color:#fff;font:16px system-ui,sans-serif;overflow:hidden;-webkit-user-select:none;user-select:none;-webkit-tap-highlight-color:transparent}
button{font:inherit}
${input.styleExtra}
</style></head><body>
${input.bodyHtml}
<script>(()=>{const ${declarations || "_unused=0"};
${input.scriptBody}
})();</script></body></html>`;
}

/** The exact LCG every seeded kind uses, in both TS evaluators and emitted JS, so replays agree. */
export function createLcg(seed: number): () => number {
  let state = seed >>> 0;
  return () => ((state = (state * 1664525 + 1013904223) >>> 0) / 4294967296);
}

/** Source text of `createLcg`'s formula, for splicing into emitted per-kind scripts. */
export const LCG_STEP_JS = "(rng=(rng*1664525+1013904223)>>>0)/4294967296";
